/**
 * TODO: Refactor this file to use CSS variables
 * @see https://tailwindcss.com/docs/customizing-colors#using-css-variables
 */

const alphaToHex: (alpha: number) => string = (alpha) => {
  const alphaBase255 = Math.round(alpha * 255);
  const hex = alphaBase255.toString(16).toUpperCase();
  return hex.length === 2 ? hex : `0${hex}`;
};

/**
 * Create hex color code for color with an alpha value
 * @param color
 * @param alpha Alpha value between 0 and 1
 * @returns Hex string of color combined with alpha value
 */
export const colorWithAlpha = (color: string, alpha: number): string => {
  return `${color}${alphaToHex(alpha)}`;
};

export const colors = {
  // Neutrals
  neutral0: '#000000',
  neutral1: '#0B0C0E',
  neutral5: '#0B0E15',
  neutral10: '#22252A',
  neutral20: '#2E3238',
  neutral30: '#3A3F46',
  neutral40: '#515862',
  neutral50: '#737982',
  neutral60: '#9B9FA6',
  neutral70: '#B8BCC2',
  neutral80: '#DDDFE4',
  neutral85: '#F3F3F4',
  neutral90: '#F4F5F6',
  neutral95: '#F9FAFA',
  neutral100: '#FFFFFF',
  // Blues
  blue10: '#0D0033',
  blue20: '#1A0066',
  blue30: '#2A00A5',
  blue40: '#3A00E5',
  blue50: '#4249FF',
  blue60: '#5C6CFF',
  blue70: '#8A95FF',
  blue80: '#EBEFFF',
  blue90: '#F6F9FE',
  // Aquas
  aqua40: '#25BCB6',
  aqua50: '#00FFCD',
  // Pinks
  pink50: '#E900EC',
  // Reds
  red40: '#AB173B',
  red50: '#E43660',
  red70: '#F8CED8',
  red80: '#FCE8F0',
  red90: '#FDF0F7',
  // Greens
  green30: '#197355',
  green40: '#24A87C',
  green70: '#D4ECDE',
  green80: '#E7F8F3',
  green90: '#E7F8F3',
  // Yellows
  yellow40: '#C5B207',
  yellow60: '#F8E53A',
  yellow70: '#F9EDB9',
  yellow80: '#FCF7DE',
  yellow90: '#FCF5EA',
  // Color used in shadows
  shadowDarkBlue: '#0A0029',
  // Color used in gradient pill
  gradientPillPrimary: '#60008D',
  // Color used in EV
  evBlue: '#00A6DE',
  evBlue2: '#049EDB',
  darkBlue100: '#3800AF',
  // Icon colors
  iconNeutral: '#0B0E15',
  logoAqua: '#15A29D',
  // Color used in Savings Pitch gradient
  red57: '#F92828',
  orange60: '#FC8237',
  yellow63: '#FED545',
  // Color used in Savings Pill gradient
  orange95: '#FFEBE5',
  // Used for meta theme-color of inventory pages
  inventoryGradientMetaTheme: '#160358',
  // Used for hero gradients
  gradientHeroBg: '#45BBDE',
  gradientBrandmark: '#D9D9D9',
  gradientBrandmark2: '#737373',
  // Colors used in PDP View Count Pill gradient
  gradientViewCountPillPrimary: '#FCF5EA',
  gradientViewCountPillSecondary: '#FFE9ED',
  // Colors used for flame icon
  flameRed: '#F92828',
  flameOrange: '#FC8237',
  flameYellowOrange: '#FFB359',
  // Colors used for Traditional Car panel icons
  redScorsese: '#EE3838',
  // Colors for brandmark sunset icon
  sunsetBlue: '#71D9D5',
  sunsetPink: '#F9DCDB',
  sunsetPurple: '#D2C6F7',
  // Colors used for color swatches on inventory filters
  swatchBrown: '#745C4E',
  swatchGold: '#EFE378',
  swatchGreen: '#318C40',
  swatchOrange: '#EA8725',
  swatchRed: '#F64E51',
  // Color used for flame icon in new PDP
  flameDeepMagenta: '#E42FB1',
  flamePinkMagenta: '#F532A4',
  flameDeepPink: '#FF3392',
  flamePinkRed: '#FF3D84',
  flameRoseRed: '#FF4775',
  flameSunsetPink: '#FF5266',
  flameRedPinkOrange: '#FF615C',
  flameRedOrange: '#FF6E52',
  flameSunsetOrange: '#FF7F4D',
  flameOrangeRed: '#FF8E47',
  flameGoldenOrange: '#FF9E42',
  flameSunburstOrange: '#FFAA42',
  // Color used for features and specs section
  grayishBlue: '#DEE3E8',
  // Color used for membership card
  membershipDarkBlue: '#2D3C5F',
  ruleGray: '#D1D1DB',
};

export const colorsAlpha = {
  neutral0Alpha1: colorWithAlpha(colors.neutral0, 0.01),
  neutral0Alpha4: colorWithAlpha(colors.neutral0, 0.04),
  neutral0Alpha5: colorWithAlpha(colors.neutral0, 0.05),
  neutral0Alpha6: colorWithAlpha(colors.neutral0, 0.06),
  neutral0Alpha7: colorWithAlpha(colors.neutral0, 0.07),
  neutral0Alpha10: colorWithAlpha(colors.neutral0, 0.1),
  neutral0Alpha45: colorWithAlpha(colors.neutral0, 0.45),
  neutral1Alpha10: colorWithAlpha(colors.neutral1, 0.1),
  neutral1Alpha15: colorWithAlpha(colors.neutral1, 0.15),
  neutral10Alpha5: colorWithAlpha(colors.neutral10, 0.05),
  neutral10Alpha10: colorWithAlpha(colors.neutral10, 0.1),
  neutral20Alpha25: colorWithAlpha(colors.neutral20, 0.25),
  neutral50Alpha25: colorWithAlpha(colors.neutral50, 0.25),
  neutral80Alpha38: colorWithAlpha(colors.neutral80, 0.38),
  neutral80Alpha70: colorWithAlpha(colors.neutral80, 0.7),
  neutral100Alpha50: colorWithAlpha(colors.neutral100, 0.5),
  shadowDarkBlueAlpha5: colorWithAlpha(colors.shadowDarkBlue, 0.05),
  shadowDarkBlueAlpha10: colorWithAlpha(colors.shadowDarkBlue, 0.1),
  blue10Alpha8: colorWithAlpha(colors.blue10, 0.08),
  blue20Alpha25: colorWithAlpha(colors.blue20, 0.25),
  blue30Alpha5: colorWithAlpha(colors.blue30, 0.05),
  blue30Alpha6: colorWithAlpha(colors.blue30, 0.06),
  blue30Alpha8: colorWithAlpha(colors.blue30, 0.08),
  blue30Alpha10: colorWithAlpha(colors.blue30, 0.1),
  blue30Alpha16: colorWithAlpha(colors.blue30, 0.16),
  blue30Alpha25: colorWithAlpha(colors.blue30, 0.25),
  blue30Alpha40: colorWithAlpha(colors.blue30, 0.4),
  blue40Alpha20: colorWithAlpha(colors.blue40, 0.2),
};

export type FlexcarColors = keyof typeof colors;
